import React, { useEffect, useContext } from "react"
import LoadingOverlay from "react-loading-overlay"
import { Badge, Button, Card, CardBody, Col, Row } from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"

// Outbound status-update channel -> friendly module name.
export const OUTBOUND_MODULE_NAMES = {
  FILE_OB_STS_UPD_Q: "FILE",
  EMAIL_OB_STS_UPD_Q: "EMAIL",
  APIV0_OB_STS_UPD_Q: "APIV0",
  APIV1_OB_STS_UPD_Q: "APIV1",
  APIV2_OB_STS_UPD_Q: "APIV2",
  TRINITY_OB_STS_UPD_Q: "TRINITY",
  "3G_OB_STS_UPD_Q": "3G",
  REVENOVA_OB_STS_UPD_Q: "REVENOVA",
  GFWD_OB_STS_UPD_Q: "GFWD",
  TURVO_OB_STS_UPD_Q: "TURVO",
  CN_OB_STS_UPD_Q: "CN",
  BROADCAST_OB_STS_UPD_Q: "Broadcast (multiple)",
}
const outboundModuleName = channel =>
  channel ? OUTBOUND_MODULE_NAMES[channel] || channel : null

const TenantConnectionDetails = ({ handleEdit, handleEditIntegration }) => {
  const {
    loading,
    tenantData,
    loadTenant,
    loadIntegration,
    integrationData,
    scac,
    isAdmin,
  } = useContext(TenantConnectionContext)

  useEffect(() => {
    // Admins load only after choosing a tenant (SCAC); others load their own.
    if (scac || !isAdmin) {
      loadTenant()
      loadIntegration()
    }
  }, [scac])

  return (
    <>
      {tenantData && (
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">Tenant Inbound Integration</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>API Type:</span>
                  </Col>
                  <Col md="8">
                    <Badge color="primary" pill>
                      {tenantData.api_type}
                    </Badge>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Outbound Module:</span>
                  </Col>
                  <Col md="8" className="d-flex align-items-center gap-2">
                    {integrationData &&
                    integrationData.tms_update_sts_channel ? (
                      <Badge color="info" pill>
                        {outboundModuleName(
                          integrationData.tms_update_sts_channel,
                        )}
                      </Badge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                    {handleEditIntegration && (
                      <Button
                        color="link"
                        className="p-0"
                        onClick={handleEditIntegration}
                      >
                        Edit
                      </Button>
                    )}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>URL:</span>
                  </Col>
                  <Col md="8">
                    <span className="text-primary">{tenantData.url}</span>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Method:</span>
                  </Col>
                  <Col md="8">
                    <Badge color="secondary" pill>
                      {tenantData.method}
                    </Badge>
                  </Col>
                </Row>

                {tenantData.method &&
                  ![
                    "ApiToken",
                    "BearerToken",
                    "CustomerToken",
                    "CustomerBasic",
                    "OAuthToken",
                  ].includes(tenantData.method) && (
                    <Row className="mb-2">
                      <Col md="4" className="text-muted">
                        <span>Custom Method:</span>
                      </Col>
                      <Col md="8">
                        <span>{tenantData.method}</span>
                      </Col>
                    </Row>
                  )}

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Username:</span>{" "}
                  </Col>
                  <Col md="8">
                    <span>{tenantData.username}</span>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Password:</span>
                  </Col>
                  <Col md="8">
                    <span>{tenantData.password}</span>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Token:</span>
                  </Col>
                  <Col md="8">
                    <span className="text-break">{tenantData.token}</span>{" "}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    {" "}
                    <span>Pick Confirmation Qualifier:</span>
                  </Col>
                  <Col md="8">
                    <span>{tenantData.pick_conf_ql}</span>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Drop Confirmation Qualifier:</span>{" "}
                  </Col>
                  <Col md="8">
                    <span>{tenantData.drop_conf_ql}</span>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Qued Reference Number Qualifier:</span>
                  </Col>
                  <Col md="8">
                    <span>{tenantData.qued_storage}</span>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md="4" className="text-muted">
                    <span>Configuration:</span>
                  </Col>
                  <Col md="8">
                    <pre
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "10px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {tenantData.config
                        ? (() => {
                            try {
                              return JSON.stringify(
                                JSON.parse(tenantData.config),
                                null,
                                2,
                              )
                            } catch (e) {
                              return tenantData.config
                            }
                          })()
                        : ""}
                    </pre>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            <div className="d-flex justify-content-end mt-3">
              <button
                className="btn btn-primary"
                size="sm"
                onClick={handleEdit}
              >
                {handleEditIntegration ? "Edit Auth" : "Edit"}
              </button>
            </div>
          </CardBody>
        </Card>
      )}
      {!tenantData && (
        <LoadingOverlay active={loading} spinner>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h5>Loading...</h5>
          </div>
        </LoadingOverlay>
      )}
    </>
  )
}

export default TenantConnectionDetails
